import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-1224" data-mp="1j8kw5gtzan26">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1225" data-mp="1j71f3o0i07zc">
        <Card data-id="element-1226" data-mp="1eb2jpggr5ppf">
          <CardContent className="p-5" data-id="element-1227" data-mp="1ebm371m1wm5k">
            <div className="flex items-start gap-3" data-id="element-1228" data-mp="1syxfx6tyyc79">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1229" data-mp="1szp8e8s9rttb">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-1230" data-mp="1o6w7jvzx95mi" />
              </div>
              <div className="min-w-0" data-id="element-1231" data-mp="21y0z0at4yrlr">
                <p className="text-sm text-gray-500 font-medium" data-id="element-1232" data-mp="1xy0huienk2b0">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-1233" data-mp="2by1lg5maxnu6">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1234" data-mp="hkjwce690ix1">
          <CardContent className="p-5" data-id="element-1235" data-mp="hk0ojqabbawq">
            <div className="flex items-start gap-3" data-id="element-1236" data-mp="ckxiq3mid0np">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1237" data-mp="cm8o27vlqggx">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-1238" data-mp="2cp01bc3m9xha" />
              </div>
              <div className="min-w-0" data-id="element-1239" data-mp="2hmjo50edriku">
                <p className="text-sm text-gray-500 font-medium" data-id="element-1240" data-mp="2lnlgcx9vqudb">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-1241" data-mp="2qn7ura1g2gnp">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-1242" data-mp="30kcfd5fn642e">
          <CardContent className="p-5" data-id="element-1243" data-mp="30jucvr2dytm1">
            <div className="flex items-start gap-3" data-id="element-1244" data-mp="30irktftbul4j">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-1245" data-mp="35bcg6lrvt6a6">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-1246" data-mp="39bwbdjsw9zto" />
              </div>
              <div className="min-w-0" data-id="element-1247" data-mp="3ed261n647x5u">
                <p className="text-sm text-gray-500 font-medium" data-id="element-1248" data-mp="3ee3owdtdhny4">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-1249" data-mp="3tfalzxue86ox">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1250" data-mp="3jhmmyvpr8d0y">
        {/* Peak Day */}
        <Card data-id="element-1251" data-mp="3fhc9kxzmukc2">
          <CardContent className="p-5" data-id="element-1252" data-mp="3fhvsagx3xcr5">
            <div className="flex items-center gap-2 mb-3" data-id="element-1253" data-mp="g0np9ite4q40">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-1254" data-mp="g28m5f36gl51">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-1255" data-mp="b3e1s8pkaoit" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1256" data-mp="68rrk8v7v20b">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-1257" data-mp="yrxbmso9f9d0">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-1258" data-mp="tzx52hdopi7g" data-mpi={i}>
                    <div className="flex items-center gap-2" data-id="element-1259" data-mp="egukyna5z7vy">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1260" data-mp="jia2w6h1oya4">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-1261" data-mp="x8dkul8w65tr">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-1262" data-mp="126ot4rjjg89v">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-1263" data-mp="sgm0isgbwwc2">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-1264" data-mp="1ky7vqx9nhkbd">
          <CardContent className="p-5" data-id="element-1265" data-mp="1kxzktax38p2t">
            <div className="flex items-center gap-2 mb-3" data-id="element-1266" data-mp="1kz13o1kcifv3">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-1267" data-mp="35o4x9l9rgnw5">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-1268" data-mp="30ptqjjeriti5" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1269" data-mp="3fegcw52t4liv">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-1270" data-mp="3ag51hpwv62yj">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-1271" data-mp="2mqap19ggiget" data-mpi={i}>
                    <div className="flex items-center gap-2" data-id="element-1272" data-mp="2wnoy2o01w2x3">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1273" data-mp="2wp9oc1fc0zp5">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-1274" data-mp="6yd7k5z638xp">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-1275" data-mp="j04pst1o26tz">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-1276" data-mp="sy29d38kzfg9">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-1277" data-mp="3rewis3xsp3cf">
          <CardContent className="p-5" data-id="element-1278" data-mp="a6i4e1s6fqv">
            <div className="flex items-center gap-2 mb-3" data-id="element-1279" data-mp="b7ware3fict">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1280" data-mp="59suymdp5tz5">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-1281" data-mp="9zzjkm87lehe" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1282" data-mp="38q9dbkbpibyb">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-1283" data-mp="3doaxy9os6jbw">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-1284" data-mp="3dn85vyfq2aue">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1285" data-mp="1qsw9h0yelr43">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-1286" data-mp="1qqs92szpra8k">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-1287" data-mp="1guymshw702dl">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-1288" data-mp="174dqj13zr9xu">
          <CardContent className="p-5" data-id="element-1289" data-mp="173kpmgrprenm">
            <div className="flex items-center gap-2 mb-3" data-id="element-1290" data-mp="172svzbhofqzz">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1291" data-mp="133tuy1z2gedt">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-1292" data-mp="y50imcvimtf8" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1293" data-mp="t54b43nm0jux">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-1294" data-mp="o6b8x7drwfa2">
              <p className="text-2xl font-bold text-blue-600" data-id="element-1295" data-mp="2czz03bexjk7i">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1296" data-mp="2h22by15igqtb">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-1297" data-mp="2321descpxvh0">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-1298" data-mp="27z1c2i78nvy3">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}